import Snackbar from '@/components/Snackbar';
import { LoadingBox } from '@/components/UI/Box';
import { BackButton, IOSSwitch } from '@/components/UI/Button';
import { GridMain } from '@/components/UI/Grid';
import { ColoredCircularProgress } from '@/components/UI/Typography';
import { Constants, statusLabels } from '@/constants/announcements';
import {
  IconArrowCircleLeftMS,
  IconCheckCircleMS,
  IconDeleteMS,
  IconEditMS,
  IconGroupsMS,
  IconScheduleMS,
  IconVisibilityMS,
  IconVisibilityOffMS,
} from '@/constants/icons';
import { GlobalContext } from '@/context/global';
import { formatDateLongMonth } from '@/helpers';
import { useContext } from 'react';

import { ConfirmDialog } from '../components/ConfirmDialog';
import { PreviewModal } from '../components/PreviewModal';
import {
  ActionsContainer,
  DeleteButton,
  DetailStatusChip,
  EditButton,
  HeaderContainer,
  InfoBar,
  MetricBadge,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricLabel,
  MetricValue,
  MetricsContainer,
  MetricsLegend,
  MetricsLegendItem,
  MetricsLegendTitle,
  NoteText,
  PageTitle,
  PreviewButton,
  TitleContainer,
  ToggleContainer,
} from './styles';
import useDetails from './useDetails';

function percentOf(value: number, total: number): string | undefined {
  if (total <= 0) return undefined;
  return `${Math.round((value / total) * 100)}%`;
}

export function AnnouncementDetails(): JSX.Element {
  const {
    announcement,
    isLoading,
    openActivate,
    openDeactivate,
    openDelete,
    openPreview,
    activeWarning,
    setOpenActivate,
    setOpenDeactivate,
    setOpenDelete,
    setOpenPreview,
    handleToggleActive,
    handleConfirmActivate,
    handleConfirmDeactivate,
    handleConfirmDelete,
    handleBack,
    handleEdit,
  } = useDetails();

  const { openSnackbar } = useContext(GlobalContext);

  if (!announcement) {
    return (
      <GridMain>
        <LoadingBox>
          <ColoredCircularProgress />
        </LoadingBox>
      </GridMain>
    );
  }

  const { metrics } = announcement;

  const metricCards = [
    {
      label: Constants.totalEligible,
      value: metrics.eligible,
      icon: IconGroupsMS,
      color: '#7C3AED',
      background: '#F3E8FF',
      badge: undefined as string | undefined,
    },
    {
      label: Constants.viewed,
      value: metrics.viewed,
      icon: IconVisibilityMS,
      color: '#2970FF',
      background: '#EFF4FF',
      badge: percentOf(metrics.viewed, metrics.eligible),
    },
    {
      label: Constants.completed,
      value: metrics.completed,
      icon: IconCheckCircleMS,
      color: '#12B76A',
      background: '#E7F8F0',
      badge: percentOf(metrics.completed, metrics.viewed),
    },
    {
      label: Constants.viewLater,
      value: metrics.read_later,
      icon: IconScheduleMS,
      color: '#F79009',
      background: '#FEF4E6',
      badge: undefined,
    },
    {
      label: Constants.notViewed,
      value: metrics.not_viewed,
      icon: IconVisibilityOffMS,
      color: '#475467',
      background: '#F2F4F7',
      badge: undefined,
    },
  ];

  const previewCards = [...announcement.cards]
    .sort((a, b) => a.order - b.order)
    .map((card) => ({
      title: card.rich_title,
      description: card.rich_text,
      videoUrl: card.video_url,
      imageUrl: card.image_url,
    }));

  return (
    <GridMain>
      <BackButton onClick={handleBack}>{IconArrowCircleLeftMS}</BackButton>

      <HeaderContainer>
        <TitleContainer>
          <PageTitle>{announcement.title}</PageTitle>
          <DetailStatusChip status={announcement.status}>
            {statusLabels[announcement.status]}
          </DetailStatusChip>
        </TitleContainer>

        <ActionsContainer>
          <PreviewButton onClick={() => setOpenPreview(true)}>
            {IconVisibilityMS}
            {Constants.preview}
          </PreviewButton>
          <EditButton onClick={handleEdit}>
            {IconEditMS}
            {Constants.edit}
          </EditButton>
          <DeleteButton onClick={() => setOpenDelete(true)}>
            {IconDeleteMS}
            {Constants.remove}
          </DeleteButton>
        </ActionsContainer>
      </HeaderContainer>

      <ToggleContainer>
        {announcement.status === 'active'
          ? statusLabels.active
          : statusLabels.inactive}
        <IOSSwitch
          checked={announcement.status === 'active'}
          disabled={isLoading}
          onChange={handleToggleActive}
        />
      </ToggleContainer>

      <InfoBar>
        <span>
          {Constants.createdAt}{' '}
          <strong>{formatDateLongMonth(announcement.created_at)}</strong>
        </span>
        <span>
          {Constants.publishedAt}{' '}
          <strong>{formatDateLongMonth(announcement.published_at)}</strong>
        </span>
        <span>
          {Constants.cards} <strong>{announcement.cards.length}</strong>
        </span>
      </InfoBar>

      <MetricsContainer>
        {metricCards.map((metric) => (
          <MetricCard key={metric.label}>
            <MetricHeader>
              <MetricIcon color={metric.color} background={metric.background}>
                {metric.icon}
              </MetricIcon>
              {metric.badge && <MetricBadge>{metric.badge}</MetricBadge>}
            </MetricHeader>
            <MetricValue>{metric.value.toLocaleString('pt-BR')}</MetricValue>
            <MetricLabel>{metric.label}</MetricLabel>
          </MetricCard>
        ))}
      </MetricsContainer>

      <MetricsLegend>
        <MetricsLegendTitle>{Constants.metricsLegendTitle}</MetricsLegendTitle>
        <MetricsLegendItem>{Constants.metricsLegendEligible}</MetricsLegendItem>
        <MetricsLegendItem>
          {Constants.metricsLegendCompleted}
        </MetricsLegendItem>
      </MetricsLegend>

      <NoteText>
        <strong>{Constants.metricsNote}</strong> {Constants.metricsNoteText}
      </NoteText>

      <ConfirmDialog
        open={openDeactivate}
        message={Constants.deactivateMessage(announcement.title)}
        cancelLabel={Constants.cancel}
        confirmLabel={Constants.deactivate}
        onCancel={() => setOpenDeactivate(false)}
        onConfirm={handleConfirmDeactivate}
      />

      <ConfirmDialog
        open={openActivate}
        message={Constants.activateMessage(announcement.title)}
        warning={activeWarning}
        cancelLabel={Constants.cancel}
        confirmLabel={Constants.activate}
        onCancel={() => setOpenActivate(false)}
        onConfirm={handleConfirmActivate}
      />

      <ConfirmDialog
        open={openDelete}
        message={Constants.deleteMessage}
        footnote={Constants.deleteWarning}
        cancelLabel={Constants.cancel}
        confirmLabel={Constants.confirmDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={handleConfirmDelete}
      />

      <PreviewModal
        open={openPreview}
        cards={previewCards}
        onClose={() => setOpenPreview(false)}
      />

      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
